/**
 * View Toggle Component
 *
 * A toggle button group for switching between table, card, and calendar views.
 */

import { HStack } from "@chakra-ui/react"
import { FiCalendar, FiGrid, FiList } from "react-icons/fi"

export type ViewMode = "table" | "cards" | "calendar"

export interface ViewToggleProps {
  value: ViewMode
  onChange: (mode: ViewMode) => void
  /** Whether to show labels */
  showLabels?: boolean
  /** Which view modes to show (defaults to table + cards) */
  modes?: ViewMode[]
}

const modeConfig: Record<
  ViewMode,
  { icon: React.ReactNode; label: string; ariaLabel: string }
> = {
  table: { icon: <FiList />, label: "Table", ariaLabel: "Table view" },
  cards: { icon: <FiGrid />, label: "Cards", ariaLabel: "Card view" },
  calendar: {
    icon: <FiCalendar />,
    label: "Calendar",
    ariaLabel: "Calendar view",
  },
}

export function ViewToggle({
  value,
  onChange,
  showLabels = false,
  modes = ["table", "cards"],
}: ViewToggleProps) {
  const baseButtonStyles = {
    padding: "6px 12px",
    fontSize: "14px",
    fontWeight: 500,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s",
  }

  return (
    <HStack
      gap={0}
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      overflow="hidden"
    >
      {modes.map((mode, index) => {
        const config = modeConfig[mode]
        const isFirst = index === 0
        const isLast = index === modes.length - 1
        const isActive = value === mode

        return (
          <button
            key={mode}
            type="button"
            style={{
              ...baseButtonStyles,
              backgroundColor: isActive ? "#0891B2" : "white",
              color: isActive ? "white" : "#4A5568",
              borderRight: !isLast ? "1px solid #E2E8F0" : "none",
              borderTopLeftRadius: isFirst ? "6px" : "0",
              borderBottomLeftRadius: isFirst ? "6px" : "0",
              borderTopRightRadius: isLast ? "6px" : "0",
              borderBottomRightRadius: isLast ? "6px" : "0",
            }}
            onClick={() => onChange(mode)}
            aria-pressed={isActive}
            aria-label={config.ariaLabel}
          >
            {config.icon}
            {showLabels && <span>{config.label}</span>}
          </button>
        )
      })}
    </HStack>
  )
}

export default ViewToggle
